<template>
    <div
        :aria-labelledby="title && bannerId"
        class="grid grid-cols-[1fr_auto] items-center gap-x-2 rounded-lg border-2 p-3 text-content-primary"
        :class="`${variant}-banner`"
    >
        <div class="grid grid-cols-[min-content_1fr] items-start gap-x-2">
            <div class="col-start-1 row-start-1 flex h-[1lh] items-center text-content-md">
                <DsIcon
                    :icon="DS_ALERT_BANNER_VARIANT_ICONS[variant]"
                    size="sm"
                />
            </div>
            <h6
                v-if="title"
                :id="bannerId"
                class="col-start-2 row-start-1 text-content-md font-semibold"
            >
                {{ title }}
            </h6>
            <div
                v-if="hasDefaultSlotContent"
                class="col-start-2 text-content-md"
                :class="title ? 'row-start-2 mt-1' : 'row-start-1'"
                data-testid="alert-banner-description"
            >
                <slot />
            </div>
        </div>
        <DsButton
            v-if="action"
            data-testid="alert-banner-action"
            size="xs"
            :variant="action.variant"
            @click="action.onClick"
        >
            {{ action.label }}
        </DsButton>
    </div>
</template>

<script setup lang="ts">
import DsButton from '@ds/button/DsButton.vue';
import DsIcon from '@ds/DsIcon.vue';
import { Comment, computed, useId, useSlots } from 'vue';
import { DS_ALERT_BANNER_VARIANT_ICONS, type DsAlertBannerProps } from './alert-banner.types';

defineProps<DsAlertBannerProps>();

const bannerId = useId();

const slots = useSlots();

// a simple check for v-if="$slots.default" is still truthy if the parent component
// passes a v-if="false" element (renders as a Comment vnode).
// instead of just checking if $slots.default exists,
// we need to check if the default slot actually has content
const hasDefaultSlotContent = computed(() => {
    const nodes = slots.default?.() ?? [];
    return nodes.some(node => node.type !== Comment);
});
</script>

<style lang="scss" scoped>
.warning-banner {
    @apply border-ds-yellow-400 bg-ds-yellow-200;
}

.error-banner {
    @apply border-ds-red-600 bg-ds-red-200;
}

.info-banner {
    @apply border-ds-blue-400 bg-ds-blue-200;
}
</style>
